/**
 * Game Type UI Helpers
 *
 * Centralized utilities for game type UI rendering (buttons, colors, text).
 * Ensures consistent styling across all game type displays.
 */

import type { RoomGameType } from 'shared-types';

/**
 * Game type button configuration
 */
export interface GameTypeButtonConfig {
  variant: 'blue' | 'yellow' | 'grey';
  arrowDirection: 'left' | 'right' | undefined;
  textKey: 'common.freePlay' | 'common.bet' | 'common.loading';
}

/**
 * Get button configuration for a given game type
 * Handles null state with grey/loading appearance
 *
 * @param gameType - Room game type ('free', 'paid', or null for loading)
 * @returns Button configuration with variant, arrow direction, and i18n text key
 *
 * @example
 * const { variant, textKey } = getGameTypeButtonConfig('paid');
 * // Returns { variant: 'yellow', arrowDirection: 'left', textKey: 'common.bet' }
 */
export function getGameTypeButtonConfig(gameType: RoomGameType | null): GameTypeButtonConfig {
  if (gameType === null) {
    return {
      variant: 'grey',
      arrowDirection: undefined,
      textKey: 'common.loading'
    };
  }

  const isPaid = gameType === 'paid';
  return {
    variant: isPaid ? 'yellow' : 'blue',
    arrowDirection: isPaid ? 'left' : 'right',
    textKey: isPaid ? 'common.bet' : 'common.freePlay'
  };
}
